/*
  Elle Exist — product page behaviour.
  Ported from the Girls Know (Dawn) theme. The buy box now runs on Palo Alto's
  product JS, so the hooks below listen to Palo Alto's theme:variant:change
  event (dispatched on the section root) instead of Dawn's pubsub.

  <gk-gallery>      thumbnail rail switches the main image and follows the variant
  .gk-pdp-root      keeps the option labels and the saving badge in step with the
                    variant — Palo Alto rewrites the prices itself
  <gk-sticky-atc>   fixed add-to-cart bar; proxies its click to the real
                    [data-add-to-cart] button so Palo Alto's cart takes it from there
  <gk-ugc-wall>     opens a social post in a lightbox with its caption and products
  <gk-review-rail>  review dots on phones
  [data-gk-play]    starts the impact band's video and hands over to native controls
*/

(function () {
  'use strict';

  var VARIANT_EVENT = 'theme:variant:change';
  var ROOT_SELECTOR = '[data-section-type="product-template"]';

  function strings() {
    return (window.theme && window.theme.strings) || {};
  }

  function formatMoney(cents) {
    var theme = window.theme;
    if (theme && typeof theme.formatMoney === 'function') {
      var withCode = theme.settings && theme.settings.currency_code_enable;
      return theme.formatMoney(cents, withCode ? theme.moneyWithCurrencyFormat : theme.moneyFormat);
    }
    return (cents / 100).toFixed(2);
  }

  /* ---- gallery ------------------------------------------------------------
     Below 990px the frame is a CSS scroll-snap slider (see gk-pdp.css) and the
     thumbnail rail is hidden; above it the slides are stacked and toggled.
     Both paths share one notion of the active media id. */

  var SLIDER_MQ = window.matchMedia('(max-width: 989px)');

  if (!customElements.get('gk-gallery')) {
    customElements.define(
      'gk-gallery',
      class GkGallery extends HTMLElement {
        connectedCallback() {
          this.frame = this.querySelector('.gk-gallery__frame');
          this.thumbs = Array.from(this.querySelectorAll('[data-gk-thumb]'));
          this.dots = Array.from(this.querySelectorAll('[data-gk-dot]'));
          this.slides = Array.from(this.querySelectorAll('[data-gk-slide]'));
          if (!this.frame || !this.slides.length) return;

          var initial = this.slides.find((s) => s.classList.contains('is-active')) || this.slides[0];
          this.activeId = initial.dataset.mediaId;
          this.connectedAt = Date.now();

          this.thumbs.forEach((thumb) => {
            thumb.addEventListener('click', () => this.show(thumb.dataset.mediaId));
          });
          this.dots.forEach((dot) => {
            dot.addEventListener('click', () => this.show(dot.dataset.mediaId));
          });

          // phones: follow the swipe, one frame per animation frame at most
          this.onScroll = () => {
            if (this.raf) return;
            this.raf = requestAnimationFrame(() => {
              this.raf = null;
              this.syncFromScroll();
            });
          };
          this.frame.addEventListener('scroll', this.onScroll, { passive: true });

          // crossing the breakpoint changes the slides from stacked to a row;
          // re-show the active one so both layouts agree on it
          this.onBreakpoint = () => this.show(this.activeId, 'auto');
          if (SLIDER_MQ.addEventListener) SLIDER_MQ.addEventListener('change', this.onBreakpoint);
          else SLIDER_MQ.addListener(this.onBreakpoint);

          // follow the variant picker: Palo Alto dispatches the change on the
          // section root with the full variant object in detail. It also fires
          // once on load, which should land instantly rather than glide.
          this.root = this.closest(ROOT_SELECTOR) || document;
          this.onVariant = (event) => {
            var variant = event.detail && event.detail.variant;
            var mediaId = variant && variant.featured_media && variant.featured_media.id;
            if (!mediaId) return;
            var settling = Date.now() - this.connectedAt < 1000;
            this.show(String(mediaId), settling ? 'auto' : 'smooth');
          };
          this.root.addEventListener(VARIANT_EVENT, this.onVariant);
        }

        disconnectedCallback() {
          if (this.frame && this.onScroll) this.frame.removeEventListener('scroll', this.onScroll);
          if (this.onBreakpoint) {
            if (SLIDER_MQ.removeEventListener) SLIDER_MQ.removeEventListener('change', this.onBreakpoint);
            else SLIDER_MQ.removeListener(this.onBreakpoint);
          }
          if (this.root && this.onVariant) this.root.removeEventListener(VARIANT_EVENT, this.onVariant);
        }

        isSlider() {
          return SLIDER_MQ.matches;
        }

        syncFromScroll() {
          if (!this.isSlider()) return;
          var width = this.frame.clientWidth;
          if (!width) return;
          var index = Math.round(this.frame.scrollLeft / width);
          var slide = this.slides[Math.max(0, Math.min(index, this.slides.length - 1))];
          if (slide && slide.dataset.mediaId !== this.activeId) this.setActive(slide.dataset.mediaId);
        }

        show(mediaId, behavior) {
          if (!mediaId) return;
          var slide = this.slides.find((s) => s.dataset.mediaId === mediaId);
          if (!slide) return;

          this.setActive(mediaId);

          // the frame is position: relative, so offsetLeft is the slide's
          // distance from the frame's own left edge
          if (this.isSlider()) {
            this.frame.scrollTo({ left: slide.offsetLeft, behavior: behavior || 'smooth' });
          }
        }

        setActive(mediaId) {
          this.activeId = mediaId;

          this.slides.forEach((s) => {
            var on = s.dataset.mediaId === mediaId;
            s.classList.toggle('is-active', on);
            // pause any video that is no longer the one in view
            s.querySelectorAll('video').forEach((video) => {
              if (!on && !video.paused) video.pause();
            });
          });

          this.thumbs.concat(this.dots).forEach((el) => {
            el.setAttribute('aria-current', el.dataset.mediaId === mediaId ? 'true' : 'false');
          });
        }
      }
    );
  }

  /* ---- buy box: option labels + saving badge -------------------------------- */

  function initBuyBox(root) {
    if (root.dataset.gkBuyBoxReady === 'true') return;
    root.dataset.gkBuyBoxReady = 'true';

    var optionLabels = Array.from(root.querySelectorAll('[data-gk-option-value]'));
    var savingAmount = root.querySelector('[data-price-off-amount]');

    root.addEventListener(VARIANT_EVENT, function (event) {
      var variant = event.detail && event.detail.variant;
      if (!variant) return;

      optionLabels.forEach(function (label) {
        var index = parseInt(label.dataset.gkOptionValue, 10);
        if (variant.options && variant.options[index] !== undefined) {
          label.textContent = variant.options[index];
        }
      });

      // Palo Alto shows and hides the badge but only writes the amount when its
      // own saving-badge setting is on, so the percentage is kept here.
      if (savingAmount && variant.compare_at_price > variant.price) {
        var pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
        savingAmount.textContent = pct + '%';
      }
    });
  }

  /* ---- sticky add to cart --------------------------------------------------- */

  if (!customElements.get('gk-sticky-atc')) {
    customElements.define(
      'gk-sticky-atc',
      class GkStickyAtc extends HTMLElement {
        connectedCallback() {
          this.bar = this.querySelector('[data-gk-sticky-bar]');
          this.cta = this.querySelector('[data-gk-sticky-submit]');
          this.priceEl = this.querySelector('[data-gk-sticky-price]');
          this.compareEl = this.querySelector('[data-gk-sticky-compare]');
          this.target = document.getElementById(this.dataset.gkTarget || '');
          this.root = this.closest(ROOT_SELECTOR) || document;
          if (!this.bar) return;

          // The real button carries data-add-to-cart; Palo Alto's cart listens
          // for that click on the document, so proxying the click reuses its
          // whole add flow — variant, errors, the drawer opening.
          if (this.cta) {
            this.onCta = () => {
              if (this.cta.disabled || !this.target) return;
              this.target.click();
            };
            this.cta.addEventListener('click', this.onCta);
          }

          this.onVariant = (event) => this.sync(event.detail && event.detail.variant);
          this.root.addEventListener(VARIANT_EVENT, this.onVariant);

          var watched = document.getElementById(this.dataset.gkWatch || '');

          // no in-page button to watch (the block was removed): just show it
          if (!watched || !('IntersectionObserver' in window)) {
            this.toggle(true);
            return;
          }

          this.observer = new IntersectionObserver(([entry]) => this.toggle(!entry.isIntersecting), {
            rootMargin: '0px 0px -40px 0px',
          });
          this.observer.observe(watched);

          this.onResize = () => {
            if (this.bar.classList.contains('is-visible')) this.reserveSpace(true);
          };
          window.addEventListener('resize', this.onResize);
        }

        disconnectedCallback() {
          this.observer?.disconnect();
          window.removeEventListener('resize', this.onResize);
          if (this.root && this.onVariant) this.root.removeEventListener(VARIANT_EVENT, this.onVariant);
          if (this.cta && this.onCta) this.cta.removeEventListener('click', this.onCta);
          this.reserveSpace(false);
        }

        sync(variant) {
          if (!this.cta) return;
          var s = strings();

          if (!variant) {
            this.cta.disabled = true;
            this.cta.textContent = s.unavailable || 'Unavailable';
            return;
          }

          this.cta.disabled = !variant.available;
          this.cta.textContent = variant.available ? s.add_to_cart || 'Add to cart' : s.sold_out || 'Sold out';

          if (this.priceEl) this.priceEl.textContent = formatMoney(variant.price);

          if (this.compareEl) {
            var onSale = variant.compare_at_price > variant.price;
            this.compareEl.classList.toggle('hidden', !onSale);
            if (onSale) this.compareEl.textContent = formatMoney(variant.compare_at_price);
          }
        }

        toggle(visible) {
          this.bar.classList.toggle('is-visible', visible);
          this.reserveSpace(visible);
        }

        // the bar is fixed, so without this it sits on top of whatever ends the page
        reserveSpace(visible) {
          document.body.style.paddingBottom = visible ? `${this.bar.offsetHeight}px` : '';
        }
      }
    );
  }

  /* ---- social wall lightbox ------------------------------------------------- */

  if (!customElements.get('gk-ugc-wall')) {
    customElements.define(
      'gk-ugc-wall',
      class GkUgcWall extends HTMLElement {
        connectedCallback() {
          // a tile with a caption or a tagged product opens the post in a lightbox
          this.addEventListener('click', this.onClick);
          var dialog = this.dialog;
          if (dialog) {
            dialog.addEventListener('click', this.onDialogClick);
            dialog.addEventListener('close', this.onDialogClose);
          }
        }

        disconnectedCallback() {
          this.removeEventListener('click', this.onClick);
          this.dialog?.removeEventListener('close', this.onDialogClose);
        }

        get dialog() {
          return document.getElementById(this.dataset.gkLightbox || '');
        }

        onClick = (event) => {
          var trigger = event.target.closest('[data-gk-ugc-open]');
          if (!trigger || !this.contains(trigger)) return;
          event.preventDefault();
          this.open(trigger);
        };

        onDialogClick = (event) => {
          if (event.target.closest('[data-gk-lightbox-close]') || event.target === event.currentTarget) {
            event.currentTarget.close();
          }
        };

        onDialogClose = (event) => {
          event.currentTarget.querySelectorAll('.gk-ugcbox').forEach((post) => {
            post.classList.remove('is-open');
            post.hidden = true;
          });
          document.documentElement.classList.remove('gk-modal-open');
          var trigger = this.lastTrigger;
          this.lastTrigger = null;
          if (trigger && document.contains(trigger)) trigger.focus();
        };

        open(trigger) {
          var dialog = this.dialog;
          var post = document.getElementById(trigger.getAttribute('aria-controls'));
          if (!dialog || !post) return;

          dialog.querySelectorAll('.gk-ugcbox').forEach((other) => {
            other.classList.remove('is-open');
            other.hidden = true;
          });

          this.lastTrigger = trigger;
          post.hidden = false;
          if (typeof dialog.showModal === 'function') {
            dialog.showModal();
          } else {
            dialog.setAttribute('open', '');
          }
          document.documentElement.classList.add('gk-modal-open');
          requestAnimationFrame(() => post.classList.add('is-open'));
        }
      }
    );
  }

  /* ---- review rail ---------------------------------------------------------- */

  if (!customElements.get('gk-review-rail')) {
    customElements.define(
      'gk-review-rail',
      class GkReviewRail extends HTMLElement {
        connectedCallback() {
          this.list = this.querySelector('[data-gk-review-list]');
          this.dots = Array.from(this.querySelectorAll('[data-gk-review-dot]'));
          if (!this.list || this.dots.length < 2) return;

          this.dots.forEach((dot, i) => {
            dot.addEventListener('click', () => {
              var card = this.list.children[i];
              if (card) this.list.scrollTo({ left: card.offsetLeft - this.list.offsetLeft, behavior: 'smooth' });
            });
          });

          // the list is only a scroller on phones; above that it is a masonry and
          // no scroll events fire, which is fine — the dots are hidden there
          this.onScroll = this.debounce(() => {
            var width = this.list.clientWidth;
            if (!width) return;
            this.setActive(Math.round(this.list.scrollLeft / width));
          }, 80);
          this.list.addEventListener('scroll', this.onScroll);
        }

        disconnectedCallback() {
          this.list?.removeEventListener('scroll', this.onScroll);
        }

        debounce(fn, wait) {
          var timer;
          return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => fn(...args), wait);
          };
        }

        setActive(index) {
          var current = Math.max(0, Math.min(index, this.dots.length - 1));
          this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === current ? 'true' : 'false'));
        }
      }
    );
  }

  /* ---- impact band video ---------------------------------------------------- */

  document.addEventListener('click', (event) => {
    var button = event.target.closest('[data-gk-play]');
    if (!button) return;
    var media = button.closest('[data-gk-media]');
    var video = media?.querySelector('video');
    if (!video) return;

    video.controls = true;
    media.classList.add('is-playing');
    video.play();
    video.addEventListener('pause', () => media.classList.remove('is-playing'), { once: true });
  });

  /* ---- boot ----------------------------------------------------------------- */

  function initAll() {
    document.querySelectorAll('.gk-pdp-root').forEach(initBuyBox);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
